import React, { useCallback, useEffect, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLogin } from "state";
import Layout from "components/Layout";
import api from "utils/api";
import { formatPrice } from "constants/massages";
import ReferralBookings from "./ReferralBookings";
import "styles/konto.css";

const EmpfehlenPage = () => {
  const dispatch = useDispatch();
  const token = useSelector((s) => s.token);
  const [referral, setReferral] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);

  const loadReferral = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data?.user) {
        dispatch(setLogin({ token, user: data.user }));
        setReferral({
          link: data.user.referralLink,
          balanceCents: data.user.referralBalanceCents || 0,
          useCount: data.user.referralUseCount || 0,
        });
      }
    } catch {
      setReferral(null);
    }
  }, [dispatch, token]);

  useEffect(() => {
    loadReferral();
  }, [loadReferral]);

  const copyReferralLink = async () => {
    if (!referral?.link) return;
    try {
      await navigator.clipboard.writeText(referral.link);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      setReferralCopied(false);
    }
  };

  const referralShareText = referral?.link
    ? `Gönn dir eine wohltuende Auszeit mit Dr. Hoffmann!
Registriere dich jetzt über meinen persönlichen Link und sichere dir deine nächste Massage:
 ${referral.link}`
    : "";
  const referralShareSubject = "Dr. Hoffmann Massage Empfehlung";
  const encodedReferralText = encodeURIComponent(referralShareText);
  const encodedReferralLink = encodeURIComponent(referral?.link || "");
  const encodedReferralSubject = encodeURIComponent(referralShareSubject);

  const shareViaInstagram = async () => {
    if (!referralShareText) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: referralShareSubject,
          text: referralShareText,
          url: referral.link,
        });
        return;
      }
      await navigator.clipboard.writeText(referralShareText);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      /* still open Instagram below */
    }
    window.open("https://www.instagram.com/direct/inbox/", "_blank", "noopener,noreferrer");
  };

  return (
    <Layout>
      <section className="konto-page">
        <div className="konto-shell">
          <header className="konto-header">
            <div>
              <h1>
                Freunde <em>empfehlen</em>
              </h1>
              <p>
                Teile deinen persönlichen Link. Wenn sich jemand darüber registriert und
                erstmals eine Massage bezahlt, erhalten ihr beide einmalig{" "}
                <strong>5&nbsp;€ Massageguthaben</strong> für eure nächste Buchung.{" "}
                <Link to="/empfehlungsprogramm">So funktioniert’s</Link>
              </p>
            </div>
          </header>

          {referral?.link ? (
            <section className="konto-referral-card" aria-label="Freunde empfehlen">
              <div className="konto-referral-stats">
                <div>
                  <span className="konto-referral-label">Guthaben</span>
                  <strong>{formatPrice(referral.balanceCents)}</strong>
                  <small>Nur für Massagen auf dieser Website</small>
                </div>
                <div>
                  <span className="konto-referral-label">Link genutzt</span>
                  <strong>{referral.useCount}×</strong>
                  <small>Registrierungen über deinen Link</small>
                </div>
              </div>
              <div className="konto-referral-link-row">
                <input
                  type="text"
                  readOnly
                  value={referral.link}
                  aria-label="Dein Empfehlungslink"
                />
                <button type="button" className="btn-primary" onClick={copyReferralLink}>
                  {referralCopied ? "Kopiert" : "Link kopieren"}
                </button>
                <div className="konto-referral-share" aria-label="Empfehlungslink teilen">
                  <a
                    href={`https://wa.me/?text=${encodedReferralText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Per WhatsApp teilen"
                    title="WhatsApp"
                  >
                    <WhatsAppIcon aria-hidden="true" />
                  </a>
                  <button
                    type="button"
                    onClick={shareViaInstagram}
                    aria-label="Per Instagram teilen"
                    title="Instagram"
                  >
                    <InstagramIcon aria-hidden="true" />
                  </button>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodedReferralLink}&quote=${encodedReferralText}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Per Facebook teilen"
                    title="Facebook"
                  >
                    <FacebookOutlinedIcon aria-hidden="true" />
                  </a>
                  <a
                    href={`mailto:?subject=${encodedReferralSubject}&body=${encodedReferralText}`}
                    aria-label="Per E-Mail teilen"
                    title="E-Mail"
                  >
                    <EmailOutlinedIcon aria-hidden="true" />
                  </a>
                </div>
              </div>
            </section>
          ) : (
            <div className="konto-empty">Empfehlungslink wird geladen…</div>
          )}
          <ReferralBookings />
        </div>
      </section>
    </Layout>
  );
};

export default EmpfehlenPage;
